import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import jsPDF from "jspdf";
import { CircuitSVG } from "../components/CircuitDrawing";
import { WaveformSVG, displayRows, WAVE_LAYOUT } from "../components/Waveforms";
import { saveBlob } from "./projects";
export const svgText = (element) => renderToStaticMarkup(element);
export async function rasterize(markup, scale = 2) {
  const svg = new DOMParser().parseFromString(
    markup,
    "image/svg+xml",
  ).documentElement;
  const w = +svg.getAttribute("width"),
    h = +svg.getAttribute("height");
  if (!w || !h) throw new Error("The diagram has no drawable dimensions.");
  const effective = Math.min(
    scale,
    16384 / w,
    16384 / h,
    Math.sqrt(30e6 / (w * h)),
  );
  const url = URL.createObjectURL(
    new Blob([markup], { type: "image/svg+xml" }),
  );
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () =>
        reject(new Error("Unable to render the diagram image."));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(w * effective);
    canvas.height = Math.ceil(h * effective);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas is unavailable in this browser.");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}
const pngBlob = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("PNG encoding failed."))),
      "image/png",
    ),
  );
export async function exportImage(element, format, name) {
  const markup = svgText(element);
  if (format === "svg") {
    saveBlob(new Blob([markup], { type: "image/svg+xml" }), `${name}.svg`);
    return;
  }
  const canvas = await rasterize(markup),
    blob = await pngBlob(canvas);
  if (format === "copy") {
    if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined")
      throw new Error(
        "Image clipboard is unavailable. Use PNG download, or open the app on localhost/HTTPS.",
      );
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
  } else saveBlob(blob, `${name}.png`);
}
export const A4 = { width: 794, height: 1123, margin: 40, contentWidth: 714, contentHeight: 1000 };
const escapeXml = text => String(text).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
function svgInfo(markup){const svg=new DOMParser().parseFromString(markup,'image/svg+xml').documentElement;return {svg,width:+svg.getAttribute('width'),height:+svg.getAttribute('height'),markup};}
function cropWave(info,x,y,w,h){
  const {left,top,right,bottom}=WAVE_LAYOUT, svg=info.svg.cloneNode(true), ns='http://www.w3.org/2000/svg';
  while(svg.firstChild)svg.removeChild(svg.firstChild);
  const width=left+w+right,height=top+h+bottom;svg.setAttribute('width',width);svg.setAttribute('height',height);svg.setAttribute('viewBox',`0 0 ${width} ${height}`);
  const white=info.svg.ownerDocument.createElementNS(ns,'rect');white.setAttribute('width',width);white.setAttribute('height',height);white.setAttribute('fill','white');svg.appendChild(white);
  const region=(dx,dw,vx)=>{const part=info.svg.cloneNode(true);part.setAttribute('x',dx);part.setAttribute('y',top);part.setAttribute('width',dw);part.setAttribute('height',h);part.setAttribute('viewBox',`${vx} ${top+y} ${dw} ${h}`);svg.appendChild(part);};
  region(0,left,0);region(left,w,left+x);return svgInfo(new XMLSerializer().serializeToString(svg));
}
function waveformBlocks(project,result,options){
  const rows=options.rows || displayRows(project,result,options.question),unit=WAVE_LAYOUT.cell/(project.timing.step||1);
  const info=svgInfo(svgText(<WaveformSVG result={result} rows={rows} question={!!options.question} unit={unit} edges={options.edges ?? project.timing.showEdges ?? true} format={project.timing.format}/>));
  const colsPerPage=Math.floor((A4.contentWidth-WAVE_LAYOUT.left-WAVE_LAYOUT.right)/WAVE_LAYOUT.cell), dataWidth=colsPerPage*WAVE_LAYOUT.cell;
  const rowsPerPage=Math.floor((A4.contentHeight-WAVE_LAYOUT.top-WAVE_LAYOUT.bottom)/WAVE_LAYOUT.rowH), dataHeight=rowsPerPage*WAVE_LAYOUT.rowH;
  if(info.width<=A4.contentWidth&&info.height<=A4.contentHeight)return [info];
  const list=[];for(let y=0;y<Math.max(1,rows.length)*WAVE_LAYOUT.rowH;y+=dataHeight)for(let x=0;x<result.duration*unit;x+=dataWidth)list.push(cropWave(info,x,y,Math.min(dataWidth,result.duration*unit-x),Math.min(dataHeight,Math.max(1,rows.length)*WAVE_LAYOUT.rowH-y)));
  return list;
}
function wrapQuestion(text){
  const ctx=document.createElement('canvas').getContext('2d');if(ctx)ctx.font='16px Arial';
  const measure=t=>ctx?ctx.measureText(t).width:t.length*8;
  const lines=[];for(const paragraph of text.replace(/\r/g,'').split('\n')){
    let line='';for(const word of paragraph.split(/\s+/)){
      if(!word)continue;
      if(line&&measure(line+' '+word)>A4.contentWidth){lines.push(line);line='';}
      let rest=word;while(measure(rest)>A4.contentWidth){let cut=1;while(cut<rest.length&&measure(rest.slice(0,cut+1))<=A4.contentWidth)cut++;lines.push(rest.slice(0,cut));rest=rest.slice(cut);}
      line+=(line?' ':'')+rest;
    }lines.push(line);
  }return lines;
}
export function buildA4Pages(project,result,options={}){
  const scope=options.scope||'both',includesCircuit=scope!=='timing',includesTiming=scope!=='circuit';
  if(includesCircuit&&!project.components.length)throw new Error('Add a circuit before exporting its diagram.');
  if(includesTiming&&!result)throw new Error('Prepare a timing diagram before exporting.');
  const graphics=[];
  if(includesCircuit)graphics.push({...svgInfo(svgText(<CircuitSVG project={project}/>)),kind:'circuit'});
  if(includesTiming)graphics.push(...waveformBlocks(project,result,options).map(g=>({...g,kind:'timing'})));
  const text=options.questionText?.trim(),lines=text?wrapQuestion(text):[],textHeight=lines.length*20;
  for(const g of graphics){const scale=Math.min(1,A4.contentWidth/g.width,A4.contentHeight/g.height);g.w=g.width*scale;g.h=g.height*scale;}
  const gaps=(graphics.length+(lines.length?1:0)-1)*16,total=textHeight+graphics.reduce((h,g)=>h+g.h,0)+gaps;
  // Fit a normal question on one A4 page when only a small uniform reduction is needed.
  if(graphics.length<=2&&total>A4.contentHeight){const scale=(A4.contentHeight-textHeight-gaps)/graphics.reduce((h,g)=>h+g.h,0);if(scale>=.8)graphics.forEach(g=>{g.w*=scale;g.h*=scale;});}
  const pages=[{blocks:[]}];let y=A4.margin;
  const next=()=>{pages.push({blocks:[]});y=A4.margin;};
  for(const line of lines){if(y+20>A4.margin+A4.contentHeight)next();pages.at(-1).blocks.push({kind:'text',text:line,x:A4.margin,y,w:A4.contentWidth,h:20});y+=20;}
  if(lines.length)y+=16;
  for(const g of graphics){if(y+g.h>A4.margin+A4.contentHeight&&pages.at(-1).blocks.length)next();pages.at(-1).blocks.push({...g,x:A4.margin+(A4.contentWidth-g.w)/2,y});y+=g.h+16;}
  return pages;
}
export function pageSvg(page){
  const body=page.blocks.map(b=>{
    if(b.kind==='text')return `<text x="${b.x}" y="${b.y+16}" font-family="Arial, sans-serif" font-size="16" fill="#111">${escapeXml(b.text)}</text>`;
    const svg=b.svg.cloneNode(true);svg.setAttribute('x',b.x);svg.setAttribute('y',b.y);svg.setAttribute('width',b.w);svg.setAttribute('height',b.h);return new XMLSerializer().serializeToString(svg);
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${A4.width}" height="${A4.height}" viewBox="0 0 ${A4.width} ${A4.height}"><rect width="100%" height="100%" fill="white"/>${body}</svg>`;
}
async function documentBlob(pages){
  const {Document,Packer,Paragraph,TextRun,ImageRun,AlignmentType}=await import('docx');
  const sections=[];
  for(const page of pages){const children=[];let end=A4.margin;
    for(const b of page.blocks){
      const before=Math.max(0,Math.round((b.y-end)*15));
      if(b.kind==='text')children.push(new Paragraph({children:[new TextRun({text:b.text,font:'Arial',size:24,color:'111111'})],spacing:{before,after:0,line:300}}));
      else {const canvas=await rasterize(b.markup,3),bytes=new Uint8Array(await (await pngBlob(canvas)).arrayBuffer());children.push(new Paragraph({alignment:AlignmentType.CENTER,spacing:{before,after:0},children:[new ImageRun({type:'png',data:bytes,transformation:{width:b.w,height:b.h},altText:{title:b.kind==='circuit'?'Circuit diagram':'Timing diagram',description:'Diagram from the current exercise',name:b.kind}})]}));}
      end=b.y+b.h;
    }
    sections.push({properties:{page:{size:{width:11906,height:16838},margin:{top:600,bottom:600,left:600,right:600}}},children});
  }
  return Packer.toBlob(new Document({creator:'',title:'',description:'',styles:{default:{document:{run:{font:'Arial',size:24},paragraph:{spacing:{after:0}}}}},sections}));
}
export async function exportDocument(project,result,options={}){
  const format=options.format||'pdf',pages=buildA4Pages(project,result,options),name=options.questionText?'question':options.scope==='circuit'?'circuit':options.scope==='timing'?'timing-diagram':'circuit-and-timing';
  if(format==='docx'){saveBlob(await documentBlob(pages),`${name}.docx`);return {pages:pages.length};}
  if(format==='pdf'){
    const pdf=new jsPDF({orientation:'portrait',unit:'mm',format:'a4',compress:true});
    for(let i=0;i<pages.length;i++){if(i)pdf.addPage();const canvas=await rasterize(pageSvg(pages[i]),2);pdf.addImage(canvas.toDataURL('image/png'),'PNG',0,0,210,297);}
    pdf.save(`${name}.pdf`);return {pages:pages.length};
  }
  if(format==='copy'){
    const page=Math.max(1,Number(options.page)||1);if(page>pages.length)throw new Error(`This export has ${pages.length} page(s). Choose a page in that range.`);
    if(!navigator.clipboard?.write||typeof ClipboardItem==='undefined')throw new Error('Image copying requires browser clipboard support and localhost or HTTPS. Download PNG instead.');
    const canvas=await rasterize(pageSvg(pages[page-1]),2);await navigator.clipboard.write([new ClipboardItem({'image/png':await pngBlob(canvas)})]);return {pages:pages.length,copiedPage:page};
  }
  if(format==='png'){for(let i=0;i<pages.length;i++){const canvas=await rasterize(pageSvg(pages[i]),2);saveBlob(await pngBlob(canvas),`${name}${pages.length>1?`-page-${i+1}`:''}.png`);}return {pages:pages.length};}
  throw new Error('Choose DOCX, PDF, PNG, or Copy PNG.');
}
export async function exportCircuit(project,format){if(format==='svg')return exportImage(<CircuitSVG project={project}/>,format,'circuit');return exportDocument(project,null,{scope:'circuit',format});}
export async function exportTiming(project,result,format,question=false){return exportDocument(project,result,{scope:'timing',format,question});}
export function paginate(width,height,pageW=1100,pageH=680){const pages=[];if(width<=1400&&height<=850)return [{x:0,y:0,w:width,h:height}];for(let y=0;y<height;y+=pageH)for(let x=0;x<width;x+=pageW)pages.push({x,y,w:Math.min(pageW,width-x),h:Math.min(pageH,height-y)});return pages;}
